/**
 * DefaultsSaveService — Debounced PATCH to save design_data to the Rails backend.
 */
export class DefaultsSaveService {
  constructor(saveUrl, csrfToken) {
    this.saveUrl = saveUrl;
    this.csrfToken = csrfToken;
    this._pendingData = {};
    this._debounceTimer = null;
    this._debounceMs = 400;
    this._inflightSave = null;
  }

  /**
   * Queue a prop update for saving. Debounces to avoid excessive requests.
   * @param {string} key - The design_data key (dot-notation)
   * @param {*} value - The new value
   */
  queueSave(key, value) {
    this._setNested(this._pendingData, key, value);
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this.flush(), this._debounceMs);
  }

  /**
   * Save variant data immediately.
   * @param {Array} variantData - The full variant_data array
   */
  saveVariants(variantData) {
    return this._doSave({ variant_data: JSON.stringify(variantData) });
  }

  /**
   * Flush all pending changes immediately. Awaits any in-flight save first
   * so callers (e.g. beforeExport) can be sure everything is committed.
   */
  async flush({ keepalive = false } = {}) {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }

    if (this._inflightSave) {
      await this._inflightSave;
    }

    if (Object.keys(this._pendingData).length > 0) {
      const data = this._pendingData;
      this._pendingData = {};
      this._inflightSave = this._doSave(
        { design_data: JSON.stringify(data) },
        { keepalive }
      );
      await this._inflightSave;
      this._inflightSave = null;
    }
  }

  async _doSave(body, { keepalive = false } = {}) {
    try {
      const response = await fetch(this.saveUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify(body),
        keepalive,
      });
      if (!response.ok) {
        console.error("Save failed:", response.status);
      }
      return response;
    } catch (error) {
      console.error("Save error:", error);
    }
  }

  _setNested(obj, path, value) {
    const keys = path.split(".");
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]] || typeof current[keys[i]] !== "object") {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
  }
}
